import React, { useMemo, useState } from 'react';
import CostSummaryComp from 'components/TariffChecker/CostSummaryComp';
import DutyComp from 'components/TariffChecker/DutyComp';
import ExpComp from 'components/TariffChecker/ExpComp';
import ProductDescComp from 'components/TariffChecker/ProductDescComp';
import TariffToolInsightsPanel from 'components/TariffChecker/TariffToolInsightsPanel';
import {
  BanGateAnswer,
  ImportBan,
  findActiveImportBan,
  importBanGateAnswer,
} from 'utils/importBans';

type SnapshotMap = Record<string, boolean>;

type QuestionOption = {
  id: string;
  label: string;
  enablesExclusions: string[];
};

type ExclusionQuestion = {
  id: string;
  prompt: string;
  helperText: string;
  options: QuestionOption[];
};

interface ReadOnlyTariffMirrorProps {
  snapshot: any;
  isSelected?: boolean;
  embedded?: boolean;
  embeddedView?: 'summary' | 'details';
}

const normalizeBoolMap = (val: any): SnapshotMap => {
  if (!val || typeof val !== 'object') {
    return {};
  }

  return Object.keys(val).reduce(
    (acc, key) => ({
      ...acc,
      [key]: Boolean(val[key]),
    }),
    {} as SnapshotMap,
  );
};

const buildExclusionQuestions = (optionalExclusions: any[]): ExclusionQuestion[] => {
  const groups: Record<string, QuestionOption[]> = {};

  optionalExclusions.forEach((candidate) => {
    const prompt = candidate?.bd_232_question;
    const answerRaw = candidate?.bd_232_answer;

    if (!prompt || !answerRaw) {
      return;
    }

    const answerId = String(answerRaw).toLowerCase();
    const answerLabel =
      answerId.length > 0
        ? `${answerId.charAt(0).toUpperCase()}${answerId.slice(1)}`
        : '';

    if (!groups[prompt]) {
      groups[prompt] = [];
    }

    let option = groups[prompt].find((item) => item.id === answerId);
    if (!option) {
      option = {
        id: answerId,
        label: answerLabel,
        enablesExclusions: [],
      };
      groups[prompt].push(option);
    }

    if (
      candidate?.codeDigits &&
      !option.enablesExclusions.includes(candidate.codeDigits)
    ) {
      option.enablesExclusions.push(candidate.codeDigits);
    }
  });

  Object.values(groups).forEach((options) => {
    const hasYes = options.some((item) => item.id === 'yes');
    const hasNo = options.some((item) => item.id === 'no');

    if (hasYes && !hasNo) {
      options.push({ id: 'no', label: 'No', enablesExclusions: [] });
    } else if (hasNo && !hasYes) {
      options.push({ id: 'yes', label: 'Yes', enablesExclusions: [] });
    }
  });

  return Object.entries(groups).map(([prompt, options], idx) => ({
    id: `snapshot-q-${idx}`,
    prompt: prompt,
    helperText:
      'Select the option that describes your product to apply the correct tariff or exclusion.',
    options: options.sort((a, b) => {
      if (a.id === 'yes') return -1;
      if (b.id === 'yes') return 1;
      return a.label.localeCompare(b.label);
    }),
  }));
};

const ReadOnlyTariffMirror: React.FC<ReadOnlyTariffMirrorProps> = ({
  snapshot,
  isSelected,
  embedded = false,
  embeddedView = 'details',
}) => {
  const [isExpand, setIsExpand] = useState(false);
  const [isExpandFlags, setIsExpandFlags] = useState(true);
  const [isExpandProd, setIsExpandProd] = useState(true);
  const [isExpandSecNotes, setIsExpandSecNotes] = useState(true);
  const [isExpandCross, setIsExpandCross] = useState(true);

  const enabledExclusions = useMemo(
    () =>
      normalizeBoolMap(
        snapshot?.results?.enabledExclusions || snapshot?.enabledExclusions,
      ),
    [snapshot],
  );
  const enabledFtas = useMemo(
    () => normalizeBoolMap(snapshot?.results?.enabledFtas || snapshot?.enabledFtas),
    [snapshot],
  );
  const exclusionQuestions = useMemo(
    () => buildExclusionQuestions(snapshot?.results?.optionalExclusions || []),
    [snapshot],
  );
  const importBans = useMemo<ImportBan[]>(() => {
    if (Array.isArray(snapshot?.importBans)) {
      return snapshot.importBans;
    }
    return Array.isArray(snapshot?.results?.importBans)
      ? snapshot.results.importBans
      : [];
  }, [snapshot]);
  const banGateAnswers = useMemo<Record<string, BanGateAnswer>>(() => {
    const answers =
      snapshot?.banGateAnswers || snapshot?.results?.banGateAnswers || {};
    return answers && typeof answers === 'object' ? answers : {};
  }, [snapshot]);
  const activeImportBan = useMemo(
    () => findActiveImportBan(importBans, banGateAnswers),
    [importBans, banGateAnswers],
  );
  const importBanQuestions = useMemo(
    () =>
      importBans.reduce(
        (
          questions: Array<{
            id: string;
            prompt: string;
            helperText: string;
            options: Array<{ id: string; label: string }>;
            selected?: string;
          }>,
          ban,
        ) => {
          ban.gates.forEach((gate) => {
            if (
              gate.keepOnAnswer === null ||
              questions.some((question) => question.id === gate.id)
            ) {
              return;
            }
            questions.push({
              id: gate.id,
              prompt: gate.question,
              helperText: `${ban.measure}. The goods are excluded from importation unless this answer lifts the ban.`,
              options: [
                { id: 'yes', label: 'Yes' },
                { id: 'no', label: 'No' },
              ],
              selected: importBanGateAnswer(gate, banGateAnswers) ?? undefined,
            });
          });
          return questions;
        },
        [],
      ),
    [importBans, banGateAnswers],
  );
  const showExpandedDetails = embedded
    ? embeddedView === 'details'
    : isExpand;

  if (!snapshot || !Array.isArray(snapshot?.results?.summaryRows)) {
    return (
      <div className={`classify-res-comp ${isSelected ? 'selected' : ''}`}>
        <div className='classify-res-comp-header'>
          <div className='classify-res-comp-header-left-icon'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
            >
              <path
                d='M4 10.6666C4 12.1393 5.19391 13.3333 6.66667 13.3333H9.33333C10.8061 13.3333 12 12.1393 12 10.6666C12 9.19383 10.8061 7.99992 9.33333 7.99992H6.66667C5.19391 7.99992 4 6.80601 4 5.33325C4 3.86049 5.19391 2.66659 6.66667 2.66659H9.33333C10.8061 2.66659 12 3.86049 12 5.33325M8 1.33325V14.6666'
                stroke='var(--text-classify)'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <div className='classify-res-comp-header-text'>Tariffs & Duties</div>
        </div>
        <p className='classification-status'>Awaiting information...</p>
      </div>
    );
  }

  return (
    <div
      className={`classify-res-comp ${isSelected ? 'selected' : ''}${
        embedded ? ' standard-workflow-tariff-mirror' : ''
      }`}
    >
      {!embedded && (
        <div className='classify-res-comp-header'>
          <div className='classify-res-comp-header-left-icon'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
            >
              <path
                d='M4 10.6666C4 12.1393 5.19391 13.3333 6.66667 13.3333H9.33333C10.8061 13.3333 12 12.1393 12 10.6666C12 9.19383 10.8061 7.99992 9.33333 7.99992H6.66667C5.19391 7.99992 4 6.80601 4 5.33325C4 3.86049 5.19391 2.66659 6.66667 2.66659H9.33333C10.8061 2.66659 12 3.86049 12 5.33325M8 1.33325V14.6666'
                stroke='var(--text-classify)'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <div className='classify-res-comp-header-text'>Tariffs & Duties</div>
          <div
            className='classify-res-comp-header-left-icon'
            style={{ paddingTop: '2px' }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
            >
              <path
                d='M13.3327 4L5.99935 11.3333L2.66602 8'
                stroke='#17B26A'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <div
            className='classify-res-comp-header-left-icon'
            style={{ paddingTop: '2px' }}
          >
            {!isExpand ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                onClick={() => setIsExpand(true)}
              >
                <path
                  d='M10 12L6 8L10 4'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                onClick={() => setIsExpand(false)}
              >
                <path
                  d='M4 6L8 10L12 6'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            )}
          </div>
        </div>
      )}

      {!showExpandedDetails ? (
        <CostSummaryComp
          tariffResults={snapshot}
          productValue={Number(snapshot?.inputs?.productValue || 0)}
          enabledExclusions={enabledExclusions}
          enabledFtas={enabledFtas}
          explanationText={String(snapshot?.explanation?.text || '')}
          aiLoading={false}
        />
      ) : (
        <>
          {/* <CostSummaryComp
            tariffResults={snapshot}
            productValue={Number(snapshot?.inputs?.productValue || 0)}
            enabledExclusions={enabledExclusions}
            enabledFtas={enabledFtas}
            explanationText={String(snapshot?.explanation?.text || '')}
            aiLoading={false}
          /> */}

          <div className='export-divider' style={{ display: 'flex' }} />

          <div className='classify-res-comp-sec-col'>
            <div className='classify-res-comp-sec'>
              <div className='classify-res-comp-sec-left'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                >
                  <path
                    d='M13.6666 4.85173L7.99998 7.99987M7.99998 7.99987L2.33331 4.85173M7.99998 7.99987L8 14.3332M14 10.7056V5.29419C14 5.06576 14 4.95155 13.9663 4.84968C13.9366 4.75956 13.8879 4.67684 13.8236 4.60705C13.7509 4.52816 13.651 4.47269 13.4514 4.36176L8.51802 1.62102C8.32895 1.51598 8.23442 1.46346 8.1343 1.44287C8.0457 1.42464 7.95431 1.42464 7.8657 1.44287C7.76559 1.46346 7.67105 1.51598 7.48198 1.62102L2.54865 4.36176C2.34896 4.47269 2.24912 4.52816 2.17642 4.60705C2.11211 4.67684 2.06343 4.75957 2.03366 4.84968C2 4.95155 2 5.06576 2 5.29419V10.7056C2 10.934 2 11.0482 2.03366 11.1501C2.06343 11.2402 2.11211 11.3229 2.17642 11.3927C2.24912 11.4716 2.34897 11.5271 2.54865 11.638L7.48198 14.3788C7.67105 14.4838 7.76559 14.5363 7.8657 14.5569C7.95431 14.5751 8.0457 14.5751 8.1343 14.5569C8.23442 14.5363 8.32895 14.4838 8.51802 14.3788L13.4514 11.638C13.651 11.5271 13.7509 11.4716 13.8236 11.3927C13.8879 11.3229 13.9366 11.2402 13.9663 11.1501C14 11.0482 14 10.934 14 10.7056Z'
                    stroke='var(--text-classify)'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M11 6.33333L5 3'
                    stroke='var(--text-classify)'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div className='classify-res-comp-sec-txt'>
                Exclusion Applicability Assistant, Risk Flags and HTS Code
                History
              </div>
              <div className='classify-res-comp-sec-left'>
                {!isExpandFlags ? (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    onClick={() => setIsExpandFlags(true)}
                  >
                    <path
                      d='M10 12L6 8L10 4'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    onClick={() => setIsExpandFlags(false)}
                  >
                    <path
                      d='M4 6L8 10L12 6'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                )}
              </div>
            </div>
            {isExpandFlags && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  marginBottom: '1.5rem',
                }}
              >
                <div>
                  <TariffToolInsightsPanel
                    htsHistory={snapshot?.insights?.history || []}
                    riskFlags={snapshot?.insights?.riskFlags || []}
                    hts_code={snapshot?.inputs?.htsCode}
                    questions={activeImportBan ? [] : exclusionQuestions}
                    enabledExclusions={enabledExclusions}
                    scopeQuestions={importBanQuestions}
                    scopeTitle='Import Ban Applicability'
                    scopeBusy
                    showScreeningLink={false}
                  />
                </div>
              </div>
            )}
          </div>

          <div className='export-divider' style={{ display: 'flex' }} />

          <div className='classify-res-comp-sec-col'>
            <div className='classify-res-comp-sec'>
              <div className='classify-res-comp-sec-left'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                >
                  <path
                    d='M13.6666 4.85173L7.99998 7.99987M7.99998 7.99987L2.33331 4.85173M7.99998 7.99987L8 14.3332M14 10.7056V5.29419C14 5.06576 14 4.95155 13.9663 4.84968C13.9366 4.75956 13.8879 4.67684 13.8236 4.60705C13.7509 4.52816 13.651 4.47269 13.4514 4.36176L8.51802 1.62102C8.32895 1.51598 8.23442 1.46346 8.1343 1.44287C8.0457 1.42464 7.95431 1.42464 7.8657 1.44287C7.76559 1.46346 7.67105 1.51598 7.48198 1.62102L2.54865 4.36176C2.34896 4.47269 2.24912 4.52816 2.17642 4.60705C2.11211 4.67684 2.06343 4.75957 2.03366 4.84968C2 4.95155 2 5.06576 2 5.29419V10.7056C2 10.934 2 11.0482 2.03366 11.1501C2.06343 11.2402 2.11211 11.3229 2.17642 11.3927C2.24912 11.4716 2.34897 11.5271 2.54865 11.638L7.48198 14.3788C7.67105 14.4838 7.76559 14.5363 7.8657 14.5569C7.95431 14.5751 8.0457 14.5751 8.1343 14.5569C8.23442 14.5363 8.32895 14.4838 8.51802 14.3788L13.4514 11.638C13.651 11.5271 13.7509 11.4716 13.8236 11.3927C13.8879 11.3229 13.9366 11.2402 13.9663 11.1501C14 11.0482 14 10.934 14 10.7056Z'
                    stroke='var(--text-classify)'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M11 6.33333L5 3'
                    stroke='var(--text-classify)'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div className='classify-res-comp-sec-txt'>Product Description</div>
              <div className='classify-res-comp-sec-left'>
                {!isExpandProd ? (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    onClick={() => setIsExpandProd(true)}
                  >
                    <path
                      d='M10 12L6 8L10 4'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    onClick={() => setIsExpandProd(false)}
                  >
                    <path
                      d='M4 6L8 10L12 6'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                )}
              </div>
            </div>
            {isExpandProd && (
              <ProductDescComp
                tariffResults={snapshot}
                productValue={Number(snapshot?.inputs?.productValue || 0)}
                dutyCost={Number(snapshot?.math?.dutyCost || 0)}
                totalCost={Number(snapshot?.math?.totalCost || 0)}
                baseCost={Number(snapshot?.math?.baseAmountDollars || 0)}
                displayAdditionalRate={snapshot?.math?.additionalDutyRateDisplay || '0%'}
                explanationText={String(snapshot?.explanation?.text || '')}
                aiLoading={false}
              />
            )}
          </div>

          <div className='export-divider' style={{ display: 'flex' }} />

          <div className='classify-res-comp-sec-col'>
            <div className='classify-res-comp-sec'>
              <div className='classify-res-comp-sec-left'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='10'
                  height='15'
                  viewBox='0 0 10 15'
                  fill='none'
                >
                  <path
                    d='M0.75 10.0833C0.75 11.5561 1.94391 12.75 3.41667 12.75H6.08333C7.55609 12.75 8.75 11.5561 8.75 10.0833C8.75 8.61057 7.55609 7.41667 6.08333 7.41667H3.41667C1.94391 7.41667 0.75 6.22276 0.75 4.75C0.75 3.27724 1.94391 2.08333 3.41667 2.08333H6.08333C7.55609 2.08333 8.75 3.27724 8.75 4.75M4.75 0.75V14.0833'
                    stroke='var(--text-classify)'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div className='classify-res-comp-sec-txt'>
                Duty Breakdown, Fees & Potential Exclusions
              </div>
              <div className='classify-res-comp-sec-left'>
                {!isExpandSecNotes ? (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    onClick={() => setIsExpandSecNotes(true)}
                  >
                    <path
                      d='M10 12L6 8L10 4'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    onClick={() => setIsExpandSecNotes(false)}
                  >
                    <path
                      d='M4 6L8 10L12 6'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                )}
              </div>
            </div>
            {isExpandSecNotes && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'stretch',
                  gap: '0px',
                }}
              >
                <DutyComp
                  tariffResults={snapshot}
                  productValue={Number(snapshot?.inputs?.productValue || 0)}
                  tariffCandidates={snapshot?.results?.optionalExclusions ?? []}
                  enabledExclusions={enabledExclusions}
                  ftaOptions={snapshot?.results?.ftaOptions ?? []}
                  enabledFtas={enabledFtas}
                  explanationText={String(snapshot?.explanation?.text || '')}
                  aiLoading={false}
                />
              </div>
            )}
          </div>

          <div className='export-divider' style={{ display: 'flex' }} />

          <div className='classify-res-comp-sec-col'>
            <div className='classify-res-comp-sec'>
              <div className='classify-res-comp-sec-left'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='15'
                  height='13'
                  viewBox='0 0 15 13'
                  fill='none'
                >
                  <path
                    d='M7.41667 11.4167H2.88333C2.1366 11.4167 1.76323 11.4167 1.47801 11.2713C1.22713 11.1435 1.02316 10.9395 0.895325 10.6887C0.75 10.4034 0.75 10.0301 0.75 9.28333V2.88333C0.75 2.1366 0.75 1.76323 0.895325 1.47801C1.02316 1.22713 1.22713 1.02316 1.47801 0.895325C1.76323 0.75 2.1366 0.75 2.88333 0.75H3.15C4.64347 0.75 5.39021 0.75 5.96064 1.04065C6.46241 1.29631 6.87035 1.70426 7.12602 2.20603C7.41667 2.77646 7.41667 3.52319 7.41667 5.01667M7.41667 11.4167V5.01667M7.41667 11.4167H11.95C12.6967 11.4167 13.0701 11.4167 13.3553 11.2713C13.6062 11.1435 13.8102 10.9395 13.938 10.6887C14.0833 10.4034 14.0833 10.0301 14.0833 9.28333V2.88333C14.0833 2.1366 14.0833 1.76323 13.938 1.47801C13.8102 1.22713 13.6062 1.02316 13.3553 0.895325C13.0701 0.75 12.6967 0.75 11.95 0.75H11.6833C10.1899 0.75 9.44312 0.75 8.87269 1.04065C8.37093 1.29631 7.96298 1.70426 7.70732 2.20603C7.41667 2.77646 7.41667 3.52319 7.41667 5.01667'
                    stroke='var(--text-classify)'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div className='classify-res-comp-sec-txt'>Additional Notes</div>
              <div className='classify-res-comp-sec-left'>
                {!isExpandCross ? (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    onClick={() => setIsExpandCross(true)}
                  >
                    <path
                      d='M10 12L6 8L10 4'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    onClick={() => setIsExpandCross(false)}
                  >
                    <path
                      d='M4 6L8 10L12 6'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                )}
              </div>
            </div>
            {isExpandCross && (
              <ExpComp
                tariffResults={snapshot}
                productValue={Number(snapshot?.inputs?.productValue || 0)}
                dutyCost={Number(snapshot?.math?.dutyCost || 0)}
                totalCost={Number(snapshot?.math?.totalCost || 0)}
                baseCost={Number(snapshot?.math?.baseAmountDollars || 0)}
                displayAdditionalRate={snapshot?.math?.additionalDutyRateDisplay || '0%'}
                tariffCandidates={snapshot?.results?.optionalExclusions ?? []}
                enabledExclusions={enabledExclusions}
                ftaOptions={snapshot?.results?.ftaOptions ?? []}
                enabledFtas={enabledFtas}
                explanationText={String(snapshot?.explanation?.text || '')}
                aiLoading={false}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ReadOnlyTariffMirror;
